import type { StarBlockerView } from 'game/boardMVC/views/blockers/StarBlockerView';
import type { Container } from 'pixi.js';
import type { TileStackModel } from '../../models/board/TileStackModel';
import type { TileStackView } from '../../views/board/TileStackView';
import { GAME_OBJECT_POOL } from '../../../GameConstants';
import { BoosterMoveModel } from '../../models/boosters/BoosterMoveModel';
import { BoosterMoveView } from '../../views/boosters/BoosterMoveView';
import type { BoosterTypeController } from './types';
import type { GameAreaController } from '../GameAreaController';
import type { GameTutorialController } from '../../../tutorials/game/GameTutorialController';
import { TutorialHand } from '../../../views/common/TutorialHand';

export class BoosterMoveController implements BoosterTypeController {
  private readonly boosterModel: BoosterMoveModel;

  public readonly isInstant = false;

  private tutorial: GameTutorialController | null = null;

  private readonly hands: Container[] = [];

  #cancel?: () => void;

  constructor(private readonly gameAreaController: GameAreaController) {
    this.boosterModel = new BoosterMoveModel(gameAreaController.boardModel);
  }

  public canExecute(): boolean {
    return this.boosterModel.canExecute();
  }

  private getStacksToHighlight(): TileStackView[] {
    if (!this.tutorial) return [];

    const stacksToHighlight = this.tutorial.getAvailableStacksForBooster();
    return stacksToHighlight
      .map((point) => {
        const stack = this.gameAreaController.boardModel.getStack(point.x, point.y);
        if (!stack) return null;
        const stackView = this.gameAreaController.getBoardView().getStackView(stack.id);
        if (!stackView || stackView.destroyed) return null;
        return stackView;
      })
      .filter(Boolean) as TileStackView[];
  }

  private addTutorialHighlights(): void {
    const stacksToHighlight = this.getStacksToHighlight();
    stacksToHighlight.forEach((view) => {
      view.highlight.add();
    });
    if (stacksToHighlight[0] && stacksToHighlight[1]) {
      const globalTarget = stacksToHighlight[1].getGlobalPosition();
      globalTarget.y += stacksToHighlight[1].getNextTileZOffset();

      this.hands.push(
        new TutorialHand()
          .props({
            parent: this.gameAreaController.getBoardView(),
            x: stacksToHighlight[0].x,
            y: stacksToHighlight[0].y + stacksToHighlight[0].getNextTileZOffset(),
          })
          .setDrag(globalTarget),
      );
    }
  }

  private removeHighlightFromStacks(): void {
    this.getStacksToHighlight().forEach((view) => {
      view.highlight.remove();
    });

    this.hands.forEach((hand) => {
      hand.destroy();
    });
    this.hands.length = 0;
  }

  public execute(onComplete: (success: boolean) => void, tutorialController: GameTutorialController | null): this {
    const {
      view: { boardView },
      dragController,
    } = this.gameAreaController;

    this.tutorial = tutorialController;
    this.boosterModel.setTutorialController(tutorialController);

    this.addTutorialHighlights();

    // Start TileStack selection / movement/
    const selectTileStack = (originStack: TileStackModel, originStackView: TileStackView): void => {
      if (!this.boosterModel.canMoveStack(originStack)) return;

      const boosterView = new BoosterMoveView(boardView, originStackView);

      this.removeHighlightFromStacks();

      // Set cancel event, reverts selection.
      this.#cancel = (): void => {
        void boosterView.reset();
        dragController.cancelDrag();
        this.removeHighlightFromStacks();
      };

      let stackTarget: TileStackModel | null = null;

      dragController.startDrag({
        container: originStackView,
        onRelease: async () => {
          boardView.setSelectionHighlight(null);
          const isValidMove = stackTarget && this.boosterModel.canExecuteOnStacks(originStack, stackTarget);
          if (isValidMove && stackTarget) {
            await boosterView.animateOriginToTarget();
            this.boosterModel.execute(originStack, stackTarget);

            // set under tile blockers visible after move
            [originStack, stackTarget].forEach((stack) => {
              const blockerView = stack.underTileBlocker && boardView.getBlockerView(stack.underTileBlocker.id);
              if (blockerView) (blockerView as StarBlockerView).showSprite();
            });
            onComplete(true);
          } else {
            await boosterView.reset();
            this.execute(onComplete, tutorialController);
          }
        },
        onUpdate: () => {
          const globalPos = originStackView.toGlobal(GAME_OBJECT_POOL.getPoint());
          const pointedStack = boardView.pointerToStack(globalPos);
          GAME_OBJECT_POOL.addPoint(globalPos);

          if (!pointedStack || pointedStack.blocker) {
            boardView.setSelectionHighlight(null);
          } else {
            boardView.setSelectionHighlight(pointedStack?.id ?? null);
          }

          if (pointedStack && this.boosterModel.canExecuteOnStacks(originStack, pointedStack)) {
            stackTarget = pointedStack;
            boosterView.setSelectedTarget(boardView.getStackView(pointedStack.id));
          } else {
            stackTarget = null;
            boosterView.setSelectedTarget(null);
          }
        },
      });
      boardView.onStackPointerDown.disconnect(selectTileStack);
    };

    boardView.onStackPointerDown.connect(selectTileStack);

    // Set cancel for selection event.
    this.#cancel = (): void => {
      boardView.onStackPointerDown.disconnect(selectTileStack);
    };

    return this;
  }

  public cancel(): void {
    this.#cancel?.();
  }
}
